import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, CreditCard, ShieldCheck, Code2, Cloud, Wrench, Bot, Lock, BookOpen, CheckCircle2, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader } from '../components/SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [5, 10, 20, 50];
const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const inp = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-3 text-white text-sm';

const DESTINOS = [
  [Code2, 'Desarrollo y nuevas funcionalidades'],
  [Cloud, 'Servidores e infraestructura'],
  [Wrench, 'Mantenimiento'],
  [Bot, 'Herramientas de IA'],
  [Lock, 'Seguridad'],
  [BookOpen, 'Recursos para la comunidad'],
];

const ThankYou = ({ amount }) => (
  <div className="sc-card p-8 md:p-12 text-center" data-testid="donacion-gracias">
    <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-5" />
    <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Gracias por apoyar Stage Crew. 💛</h2>
    <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">Tu aportación{amount ? ` de ${eur(amount)}` : ''} ayudará a que podamos seguir manteniendo y mejorando la plataforma.</p>
  </div>
);

const Counters = ({ totals }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="donacion-counters">
    <div className="sc-card p-5 text-center">
      <div className="sc-mono-eyebrow text-amber-500 mb-2">Total donado</div>
      <div className="text-2xl font-bold text-white" data-testid="donacion-total-donado">{eur(totals?.donated_total)}</div>
    </div>
    <div className="sc-card p-5 text-center">
      <div className="sc-mono-eyebrow text-slate-400 mb-2">Gastado en Stage Crew</div>
      <div className="text-2xl font-bold text-white" data-testid="donacion-total-gastado">{eur(totals?.spent_total)}</div>
    </div>
    <div className="sc-card p-5 text-center">
      <div className="sc-mono-eyebrow text-slate-400 mb-2">Última actualización</div>
      <div className="text-2xl font-bold text-white" data-testid="donacion-last-update">{totals?.last_update ? totals.last_update.split('-').reverse().join('/') : '—'}</div>
    </div>
  </div>
);

const Inner = () => {
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [totals, setTotals] = useState(null);
  const [selected, setSelected] = useState(10);
  const [custom, setCustom] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // {amount}
  const amount = custom ? Number(custom) : selected;

  const loadTotals = useCallback(() => { fetch(`${API}/donations/total`).then((r) => r.json()).then(setTotals).catch(() => {}); }, []);

  useEffect(() => { fetch(`${API}/donations/config`).then((r) => r.json()).then(setConfig).catch(() => {}); loadTotals(); }, [loadTotals]);

  // Handle return from Stripe: poll status, confirm, show thank-you.
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) { if (params.get('cancelado')) toast('Pago cancelado. Puedes intentarlo cuando quieras.'); return; }
    let tries = 0; let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await fetch(`${API}/donations/status/${sid}`);
        const d = await res.json();
        if (d.payment_status === 'confirmed') { setConfirmed({ amount: d.amount }); loadTotals(); return; }
      } catch { /* retry */ }
      if (tries++ < 6) setTimeout(poll, 2000); else toast('Estamos confirmando tu pago. El total se actualizará en unos momentos.');
    };
    poll();
    return () => { active = false; };
  }, [params, loadTotals]);

  const donateCard = async () => {
    if (!amount || amount < 1) { toast.error('Elige o escribe una cantidad válida'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/donations/checkout`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, origin_url: window.location.origin, anonymous, donor_name: anonymous ? null : (donorName || null) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'No se pudo iniciar el pago');
      window.location.href = d.checkout_url;
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  const donatePaypal = () => {
    if (!config?.paypal_url) { toast('El pago con PayPal estará disponible en breve.'); return; }
    const sep = config.paypal_url.includes('?') ? '&' : '?';
    const url = amount && amount >= 1 ? `${config.paypal_url}${sep}amount=${amount}` : config.paypal_url;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6"><ThankYou amount={confirmed.amount} /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader title="Donaciones" subtitle="Stage Crew es gratis. Y queremos que siga siendo así." testId="donaciones-header" />

        <div className="sc-card p-6 md:p-8 mb-10" data-testid="donacion-intro">
          <p className="text-sm text-slate-400 leading-relaxed mb-3">Stage Crew es una plataforma <span className="text-slate-200">gratuita</span>. No necesitas donar para usarla: puedes buscar y publicar ofertas, participar en el foro, acceder a la biblioteca y a todo lo demás sin coste.</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-3">Aun así, crear y mantener la plataforma tiene costes reales —servidores, infraestructura, herramientas, desarrollo, mantenimiento e inteligencia artificial— y muchas horas de trabajo detrás.</p>
          <p className="text-sm text-slate-400 leading-relaxed">Las donaciones son <span className="text-slate-200">completamente voluntarias</span> y cualquier cantidad ayuda. Todo el dinero recibido se destina exclusivamente a mantener, mejorar y desarrollar Stage Crew.</p>
        </div>

        <Counters totals={totals} />

        <div className="sc-card p-6 md:p-8 mb-10" data-testid="donacion-form">
          <div className="flex items-center gap-2 mb-6"><Heart size={18} className="text-amber-500" /><h2 className="sc-mono-eyebrow text-amber-500">Elige tu aportación</h2></div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="donacion-presets">
            {PRESETS.map((v) => { const active = !custom && Number(selected) === v; return (
              <button key={v} type="button" data-testid={`donacion-amount-${v}`} onClick={() => { setSelected(v); setCustom(''); }}
                className={`rounded-lg border px-4 py-4 text-lg font-bold transition-colors sc-focus ${active ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-[#2A2F3D] text-white hover:border-amber-500/50'}`}>{v} €</button>
            ); })}
          </div>
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">✏️ Otra cantidad (€)</label>
          <input data-testid="donacion-custom-amount" type="number" min="1" step="1" inputMode="numeric" value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Escribe una cantidad libre" className={inp} />

          <div className="mt-5 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="donacion-anon-label">
              <input type="checkbox" data-testid="donacion-anonymous" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="w-4 h-4 accent-amber-500" />
              Quiero hacer esta donación de forma anónima
            </label>
            {!anonymous && (
              <input data-testid="donacion-donor-name" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Tu nombre (opcional)" className={inp} />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
            <button type="button" data-testid="donacion-card-btn" onClick={donateCard} disabled={busy}
              className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-bold px-6 py-4 rounded-lg transition-colors">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Donar {amount ? eur(amount) : ''} con tarjeta
            </button>
            <button type="button" data-testid="donacion-paypal-btn" onClick={donatePaypal}
              className="inline-flex items-center justify-center gap-2 border border-[#2A2F3D] hover:border-amber-500/60 text-white font-bold px-6 py-4 rounded-lg transition-colors">
              <span className="text-[#00457C] bg-white rounded px-1.5 py-0.5 text-xs font-black">Pay<span className="text-[#0079C1]">Pal</span></span> Donar con PayPal
            </button>
          </div>
          <div className="flex items-start gap-2 mt-4 text-xs text-slate-500 leading-relaxed" data-testid="donacion-secure-note">
            <ShieldCheck size={14} className="text-emerald-400 shrink-0 mt-0.5" />
            El pago se procesa de forma segura en Stripe o PayPal. Stage Crew nunca almacena los datos de tu tarjeta.
          </div>
        </div>

        <div className="sc-card p-6 md:p-8 mb-10" data-testid="donacion-destino">
          <h2 className="sc-mono-eyebrow text-amber-500 mb-4">¿A dónde va el dinero?</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-5">Queremos ser transparentes: el dinero recibido mediante donaciones se destinará al mantenimiento, funcionamiento y mejora de Stage Crew.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DESTINOS.map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-3 border border-[#2A2F3D] rounded-lg p-3">
                <Icon size={16} className="text-amber-500 shrink-0" /><span className="text-sm text-slate-300">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-sm text-slate-400 leading-relaxed max-w-2xl mx-auto pb-16" data-testid="donacion-final">
          <p className="mb-3">Gracias por formar parte de Stage Crew. Ya sea utilizando la plataforma, publicando una oferta, compartiendo una oportunidad, ayudando a otro profesional o haciendo una pequeña aportación, cada gesto ayuda a que este proyecto siga creciendo.</p>
          <p className="text-slate-300">Stage Crew es una plataforma construida para el sector y con el sector.</p>
          <p className="text-amber-500 mt-4 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>abraçada tècnica</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const DonacionesPage = () => <Inner />;
export default DonacionesPage;
